import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewEncapsulation, computed, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest, interval } from 'rxjs';
import { map } from 'rxjs/operators';
import { MsalHelperService } from '../services/msal-helper.service';
import { ApiService } from '../services/api.service';
import { PaymentService } from '../services/payment.service';
import { ContextService } from '../services/context.service';

@Component({
    selector: 'app-teams-settings',
    templateUrl: './teams-settings.component.html',
    styleUrls: ['./teams-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class TeamsSettingsComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  public license$ = new BehaviorSubject<any>(null);
  public user$ = new BehaviorSubject<any>(null);
  public accounts$ = new BehaviorSubject<any[]>([]);
  public rules$ = new BehaviorSubject<any[]>([]);
  public scans$ = new BehaviorSubject<any[]>([]);
  public scansTimespan$: Observable<any>;
  public workspaceInfo: any = null;
  public contextState: 'loading' | 'noStorage' | 'noLicense' | 'ready' = 'loading';
  public workspace;
  public workspaceFlowCapacity = signal<number | null>(null);

  constructor(
    private paymentService: PaymentService,
    private msalHelperService: MsalHelperService,
    private monitorService: ApiService,
    private contextService: ContextService
  ) {
    this.workspace = this.paymentService.workspace;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.msalHelperService.user$.subscribe((user) => {
        if (user) this.user$.next(user);
      })
    );

    this.contextService.load().then((ctx) => {
      if (!ctx?.workspace) {
        this.contextState = 'noLicense';
        return;
      }
      if (!ctx.hasWorkspaceStorage) {
        this.contextState = 'noStorage';
        return;
      }
      if (!ctx.hasTeamsEntitlement) {
        this.contextState = 'noLicense';
        return;
      }

      this.license$.next(ctx.clarityUser);
      this.accounts$.next(ctx.accounts);
      this.workspaceInfo = ctx.workspace;
      this.workspaceFlowCapacity.set(ctx.teamsMonitorLimit ?? null);
      this.contextState = 'ready';

      this.refreshStats();
      this.scansTimespan$ = combineLatest([interval(1000), this.scans$]).pipe(map(([_t, scans]) => {
        return (scans || []).map((stats) => {
          try {
            const startMs = new Date(stats.start).valueOf();
            const hasEnd = !!stats.end;
            const endMs = hasEnd ? new Date(stats.end).valueOf() : null;

            // Actively running only when there is no end date at all
            if (!hasEnd) {
              const diffMs = Date.now() - startMs;
              const mins = Math.floor(diffMs / 1000 / 60);
              const secs = Math.floor((diffMs / 1000) % 60);
              const agoMs = Date.now() - startMs;
              const agoH = Math.floor(agoMs / 1000 / 60 / 60);
              const agoD = Math.floor(agoMs / 1000 / 60 / 60 / 24);
              const when = agoD >= 1
                ? new Date(startMs).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                : agoH >= 1 ? `${agoH}h ago`
                : `${Math.floor(agoMs / 1000 / 60)}m ago`;
              return { duration: `${mins}m ${secs}s`, when, inProgress: true };
            }

            // end is set — static, use end - start for duration
            const diffMs = Math.abs(endMs - startMs);
            const mins = Math.floor(diffMs / 1000 / 60);
            const secs = Math.floor((diffMs / 1000) % 60);
            const duration = `${mins}m ${secs}s`;

            const agoMs = Date.now() - endMs;
            const agoH = Math.floor(agoMs / 1000 / 60 / 60);
            const agoD = Math.floor(agoMs / 1000 / 60 / 60 / 24);
            const when = agoD >= 1
              ? new Date(endMs).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
              : agoH >= 1 ? `${agoH}h ago`
              : `${Math.floor(agoMs / 1000 / 60)}m ago`;

            return { duration, when, inProgress: false };
          } catch {
            return { duration: null, when: null, inProgress: false };
          }
        });
      }));

      this.monitorService.watchConfigurations().then((query) => {
        this.subscription.add(
          query.subscribe((result) => {
            const configs = result.data;
            this.rules$.next(configs.rules);
          })
        );
      });
    }).catch(() => {
      this.contextState = 'noLicense';
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public refreshStats(): void {
    this.monitorService.queryScansHttp().then((scans) => {
      this.scans$.next(scans);

      if (!scans) {
        setTimeout(() => {
          this.refreshStats();
        }, 30000);
      }
    });
  }

  public findAccount(oid) {
    return this.accounts$.value.find((a) => a.oid == oid);
  }

  public getAccountCount(): number {
    return (this.accounts$.value || []).length;
  }
}
